<template>
  <div
    v-clickoutside="close"
    :aria-expanded="suggestionVisible"
    :aria-owns="id"
    class="rt-autocomplete el-autocomplete"
    aria-haspopup="listbox"
    role="combobox"
    @mouseenter="inputHovering = true"
    @mouseleave="inputHovering = false"
  >
    <el-input
      ref="input"
      v-model="currentValue"
      v-bind="[$props, $attrs]"
      :clearable="false"
      :class="{ 'is-focus': activated}"
      @input="handleInput"
      @change="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
      @clear="handleClear"
      @keydown.up.native.prevent="highlight(highlightedIndex - 1)"
      @keydown.down.native.prevent="highlight(highlightedIndex + 1)"
      @keydown.enter.native="handleKeyEnter"
      @keydown.native.tab="close"
    >
      <template
        v-if="$slots.prepend"
        slot="prepend"
      >
        <slot name="prepend"></slot>
      </template>
      <template
        v-if="$slots.append"
        slot="append"
      >
        <slot name="append"></slot>
      </template>
      <template
        v-if="$slots.prefix || $scopedSlots.prefix"
        slot="prefix"
      >
        <slot name="prefix"></slot>
      </template>
    </el-input>
    <rt-icon
      v-if="!showClear && showSuffixIcon"
      slot="suffix"
      :color="SCSS_VARIABLES.COLOR_INFO"
      name="rt-base-record-lined"
      class="rt-autocomplete-righticon"
    />
    <rt-icon
      v-if="showClear"
      slot="suffix"
      :color="SCSS_VARIABLES.COLOR_INFO"
      :style="computedClearIconStyle"
      class="rt-autocomplete-clearicon"
      name="rt-base-close_fill"
      @click="handleClear"
    />
    <el-autocomplete-suggestions
      :id="id"
      ref="suggestions"
      :class="[popperClass ? popperClass : '']"
      :popper-options="popperOptions"
      :append-to-body="popperAppendToBody"
      :placement="placement"
      visible-arrow
    >
      <div v-if="suggestions.length">
        <li
          v-for="(item, index) in suggestions"
          :id="`${id}-item-${index}`"
          :key="index"
          :class="{ highlighted: highlightedIndex === index }"
          :aria-selected="highlightedIndex === index"
          role="option"
          class="rt-autocomplete-suggestion-item"
          @click="select(item)"
        >
          <slot
            v-if="item[valueKey]"
            :item="item"
            :index="index"
          >
            <div
              class="rt-autocomplete-suggestion-item-label"
              v-html="highlightQueryString(item[valueKey])"
            ></div>
            <template v-if="!hideOptionDesc && item[optionDescField]">
              <div
                v-if="typeof item[optionDescField] === 'string'"
                class="rt-autocomplete-suggestion-item-desc"
              >
                {{ item[optionDescField] }}
              </div>
              <template v-else>
                <div
                  v-for="(n, i) in item[optionDescField]"
                  :key="n.label + '-' + n.value + '-' + i"
                  class="rt-autocomplete-suggestion-item-desc"
                >
                  {{ n.label + ': ' + n.value }}
                </div>
              </template>
            </template>
          </slot>
        </li>
      </div>
      <rt-default-image
        v-else-if="!loading && !suggestions.length && fetchSuggestions"
        text
      >
        <div slot="describe">
          {{ $tx(`未找到${rtFormItem.label}`, 'xpc.common.notFound', {label: rtFormItem.label}) }}
        </div>
      </rt-default-image>
      <rt-default-image
        v-else-if="!fetchSuggestions && currentValue"
        text
      >
        <div slot="describe">
          {{ $tx('暂无数据', 'xpc.defaultImage.noData') }}
        </div>
      </rt-default-image>
    </el-autocomplete-suggestions>
  </div>
</template>
<script>
import debounce from 'throttle-debounce/debounce';
import ElInput from 'element-ui/lib/input';
import Clickoutside from 'element-ui/lib/utils/clickoutside';
import ElAutocompleteSuggestions from './autocomplete-suggestions.vue';
import Emitter from 'element-ui/lib/mixins/emitter';
import Migrating from 'element-ui/lib/mixins/migrating';
import { generateId } from 'element-ui/lib/utils/util';
import Focus from 'element-ui/lib/mixins/focus';
import RtIcon from 'vue-xpc/src/components/icon/rtIcon.vue';
import xpcConfig from 'vue-xpc/src/config';

export default {
  name: 'RtAutocomplete',

  mixins: [Emitter, Focus('input'), Migrating],

  inheritAttrs: false,

  componentName: 'ElAutocomplete',

  // eslint-disable-next-line vue/order-in-components
  components: {
    ElInput,
    ElAutocompleteSuggestions,
    RtIcon
  },

  inject: {
    elForm: {
      default: ''
    },
    rtFormItem: {
      default: ''
    }
  },

  // eslint-disable-next-line vue/order-in-components
  directives: { Clickoutside },

  props: {
    valueKey: {
      type: String,
      default: 'value'
    },
    popperClass: String,
    popperOptions: Object,
    placeholder: String,
    disabled: Boolean,
    name: String,
    size: String,
    value: String,
    maxlength: Number,
    minlength: Number,
    autofocus: Boolean,
    fetchSuggestions: Function,
    triggerOnFocus: {
      type: Boolean,
      default: true
    },
    customItem: String,
    selectWhenUnmatched: {
      type: Boolean,
      default: false
    },
    prefixIcon: String,
    suffixIcon: String,
    label: String,
    debounce: {
      type: Number,
      default: 300
    },
    placement: {
      type: String,
      default: 'bottom-start'
    },
    hideLoading: Boolean,
    popperAppendToBody: {
      type: Boolean,
      default: true
    },
    highlightFirstItem: {
      type: Boolean,
      default: false
    },
    // 关键字亮高
    highlightKey: {
      type: Boolean,
      default: true
    },
    // 清空按钮
    clearable: {
      type: Boolean,
      default: undefined
    },
    // 自定义清空按钮图标
    clearIconName: {
      type: String,
      default: undefined
    },
    // 自定义清空按钮位置
    clearIconRight: {
      type: Number,
      default: 0
    },
    /**
     * @public 是否展示后侧图标
     */
    showSuffixIcon: {
      type: Boolean,
      default: true
    },
    // 选项中是否启用描述
    hideOptionDesc: {
      type: Boolean,
      default: false
    },
    // 选项数组中的描述字段名称
    optionDescField: {
      type: String,
      default: 'description'
    }
  },
  data () {
    return {
      currentValue: this.value,
      activated: false,
      suggestions: [],
      loading: false,
      highlightedIndex: -1,
      suggestionDisabled: false,
      queryString: '',
      inputHovering: false // 鼠标悬浮在输入框上
    };
  },
  computed: {
    computedClearable () {
      return this.clearable === undefined
        ? this.elForm &&
            this.elForm.$attrs &&
            this.elForm.$attrs['all-clearable']
        : this.clearable;
    },
    suggestionVisible () {
      const suggestions = this.suggestions;
      let isValidData = Array.isArray(suggestions) && suggestions.length > 0;
      return (
        (isValidData || this.loading || this.currentValue) && this.activated
      );
    },
    id () {
      return `el-autocomplete-${generateId()}`;
    },
    computedClearIconStyle () {
      let style = {};
      if (this.computedClearable) {
        style['marginRight'] =
          this.clearIconRight ||
          xpcConfig.get('clearicon.clearIconRight') + 'px';
      }
      return style;
    },
    showClear () {
      return (
        this.currentValue &&
        this.inputHovering &&
        !this.disabled &&
        this.computedClearable
      );
    },
    computedIconName () {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    }
  },
  watch: {
    value (newValue) {
      if (newValue && this.fetchSuggestions) {
        this.loading = true;
      }
      this.currentValue = newValue;
    },
    suggestionVisible (val) {
      let $input = this.getInput();
      if ($input) {
        this.broadcast('ElAutocompleteSuggestions', 'visible', [
          val,
          $input.offsetWidth
        ]);
      }
    },
    suggestions (val) {
      if (val.length > 0) {
        this.$refs.suggestions.showPopper = true;
      } else if (val.length === 0 && !this.currentValue) {
        this.$refs.suggestions.showPopper = false;
      }
    }
  },
  mounted () {
    this.debouncedGetData = debounce(this.debounce, this.getData);
    this.$on('item-click', item => {
      this.select(item);
    });
    let $input = this.getInput();
    $input.setAttribute('role', 'textbox');
    $input.setAttribute('aria-autocomplete', 'list');
    $input.setAttribute('aria-controls', 'id');
    $input.setAttribute(
      'aria-activedescendant',
      `${this.id}-item-${this.highlightedIndex}`
    );
  },
  beforeDestroy () {
    this.$refs.suggestions.$destroy();
  },
  methods: {
    getMigratingConfig () {
      return {
        props: {
          'custom-item': 'custom-item is removed, use scoped slot instead.',
          props: 'props is removed, use value-key instead.'
        }
      };
    },
    getData (queryString) {
      if (this.suggestionDisabled) {
        return;
      }
      this.queryString = queryString;
      if (this.fetchSuggestions) {
        this.loading = true;
      }
      this.fetchSuggestions &&
        this.fetchSuggestions(queryString, suggestions => {
          this.loading = false;
          if (this.suggestionDisabled) {
            return;
          }
          if (Array.isArray(suggestions)) {
            this.suggestions = suggestions;
            this.highlightedIndex = this.highlightFirstItem ? 0 : -1;
          } else {
            console.error(
              '[Element Error][Autocomplete]autocomplete suggestions must be an array'
            );
          }
        });
    },
    handleInput (value) {
      this.$emit('input', value);
      this.suggestionDisabled = false;
      if (!this.triggerOnFocus && !value) {
        this.suggestionDisabled = true;
        this.suggestions = [];
        return;
      }
      this.debouncedGetData(value);
    },
    handleChange (value) {
      this.$emit('change', value);
    },
    handleFocus (event) {
      this.activated = true;
      this.$emit('focus', event);
      if (this.triggerOnFocus) {
        this.debouncedGetData(this.currentValue);
      } else if (!this.triggerOnFocus && this.suggestions && this.suggestions.length === 0) {
        this.$nextTick(() => {
          this.$refs.suggestions.showPopper = false;
        })
      }
    },
    handleClearQueryString () {
      this.queryString = '';
      this.suggestions = [];
    },
    handleBlur (event) {
      this.$emit('blur', event);
    },
    handleClear () {
      this.activated = false;
      this.currentValue = '';
      this.suggestions = [];
    },
    close (e) {
      this.activated = false;
    },
    handleKeyEnter (e) {
      if (
        this.suggestionVisible &&
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.suggestions.length
      ) {
        e.preventDefault();
        this.select(this.suggestions[this.highlightedIndex]);
      } else if (this.selectWhenUnmatched) {
        this.$emit('select', { value: this.currentValue });
        this.$nextTick(_ => {
          this.suggestions = [];
          this.highlightedIndex = -1;
        });
      }
    },
    select (item) {
      this.$emit('input', item[this.valueKey]);
      this.$emit('select', item);
      this.$nextTick(_ => {
        this.suggestions = [];
        this.highlightedIndex = -1;
        this.$refs.suggestions.showPopper = false;
      });
    },
    highlight (index) {
      if (!this.suggestionVisible || this.loading) {
        return;
      }
      if (index < 0) {
        this.highlightedIndex = -1;
        return;
      }
      if (index >= this.suggestions.length) {
        index = this.suggestions.length - 1;
      }
      const suggestion = this.$refs.suggestions.$el.querySelector(
        '.el-autocomplete-suggestion__wrap'
      );
      const suggestionList = suggestion.querySelectorAll(
        '.el-autocomplete-suggestion__list li'
      );

      let highlightItem = suggestionList[index];
      let scrollTop = suggestion.scrollTop;
      let offsetTop = highlightItem.offsetTop;

      if (
        offsetTop + highlightItem.scrollHeight >
        scrollTop + suggestion.clientHeight
      ) {
        suggestion.scrollTop += highlightItem.scrollHeight;
      }
      if (offsetTop < scrollTop) {
        suggestion.scrollTop -= highlightItem.scrollHeight;
      }
      this.highlightedIndex = index;
      let $input = this.getInput();
      $input.setAttribute(
        'aria-activedescendant',
        `${this.id}-item-${this.highlightedIndex}`
      );
    },
    getInput () {
      return this.$refs.input.getInput();
    },
    /**
     * @param {String} str
     */
    // 关键字高亮
    highlightQueryString (str) {
      str = (str === null || str === undefined) ? '' : str;
      str = str.toString();
      if (!this.highlightKey) {
        return str;
      } else {
        let index = str.indexOf(this.queryString);
        let queryStringKey = str.slice(index, index + this.queryString.length);
        return str.replace(
          queryStringKey,
          `<span style="color:${this.SCSS_VARIABLES.COLOR_WARNING}">${queryStringKey}</span>`
        );
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// autocomplete.vue?586f2b18